import { useMemo } from 'react'
import { gql, useQuery } from '@apollo/client'
import moment from 'moment'

import { useSites } from 'store'
import { filterStatsByTimeRange, generateRanges } from '../use-utils'

const OPERATION_TRAFFIC = gql`
  query TrafficDistributionGroupByNetwork($sfIds: [ID], $pastStartsAt: String, $currentStartsAt: String, $currentEndsAt: String, $pastEndsAt: String) {
    current: trafficGraph(sf_ids: $sfIds, starts_at: $currentStartsAt, ends_at: $currentEndsAt) {
      requested_at
      active_users
      download
      upload
    }
    past: trafficGraph(sf_ids: $sfIds, starts_at: $pastStartsAt, ends_at: $pastEndsAt) {
      requested_at
      active_users
      download
      upload
    }
  }
`

export const generateGraphNetworks = ({stats, ranges}) => {
  return ranges.map((range) => {
    const statsInRange = filterStatsByTimeRange(stats, range.from, range.to)
    const totalActiveUsers = statsInRange.reduce((acc, stat) => {
      return acc + stat.active_users
    }, 0)
    const totalDownload = statsInRange.reduce((acc, stat) => {
      return acc + stat.download
    }, 0)
    const totalUpload = statsInRange.reduce((acc, stat) => {
      return acc + stat.upload
    }, 0)
    return {
      date: range.from,
      active_users: totalActiveUsers,
      upload: totalUpload,
      download: totalDownload,
    }
  })
}

const useGraph = () => {
  const [{selected}] = useSites()
  
  const dates = useMemo(() => {
    const currentStartsAt = moment().subtract(12, 'months').startOf('month')
    const currentEndsAt = moment().subtract(1, 'months').endOf('month')
    
    const pastStartsAt = moment().subtract(24, 'months').startOf('month')
    const pastEndsAt = moment().subtract(13, 'months').endOf('month')
    
    return {
      currentStartsAt,
      currentEndsAt,
      pastStartsAt,
      pastEndsAt
    }
  }, [])

  const { data, loading } = useQuery(OPERATION_TRAFFIC, {
    variables: {
      sfIds: selected.map(({ sf_id }) => sf_id),
    currentStartsAt: dates.currentStartsAt,
      currentEndsAt: dates.currentEndsAt,
      pastStartsAt: dates.pastStartsAt,
      pastEndsAt: dates.pastEndsAt
    }
  })

  const formattedData = useMemo(() => {
    if (!data) {
      return {
        current: [],
        past: []
      }
    }
    return {
      current: data.current.map(values => ({
        requested_at: values.requested_at,
        download: parseInt(values.download) / 1000,
        upload: parseInt(values.upload) / 1000,
        active_users: parseInt(values.active_users),
      })),
      past: data.past.map(values => ({
        requested_at: values.requested_at,
        download: parseInt(values.download) / 1000,
        upload: parseInt(values.upload) / 1000,
        active_users: parseInt(values.active_users),
      })),
    }
  }, [data])
  const graph = useMemo(() => {
    const currentRanges = generateRanges({startsAt: dates.currentStartsAt, endsAt: dates.currentEndsAt, scale: 'month'})
    const pastRanges = generateRanges({startsAt: dates.pastStartsAt, endsAt: dates.pastEndsAt, scale: 'month'})
    const currentData = generateGraphNetworks({stats: formattedData.current, ranges: currentRanges})
    const pastData = generateGraphNetworks({stats: formattedData.past, ranges: pastRanges})
    return currentData.map((current, i) => {
      const consumptionCurrent = current.download + current.upload
      const consumptionPast = pastData[i].download + pastData[i].upload
      const activeUsersCurrent = current.active_users
      const activeUsersPast = pastData[i].active_users
      const percentageConsumption = ((consumptionCurrent - consumptionPast) / consumptionPast) * 100
      const percentageActiveUsers = ((activeUsersCurrent - activeUsersPast) / activeUsersPast) * 100
      return {
        date: current.date,
        consumption_current_year: consumptionCurrent,
        consumption_past_year: consumptionPast,
        percentage_consumption: percentageConsumption,
        active_users_current_year: activeUsersCurrent,
        active_users_past_year: activeUsersPast,
        percentage_active_users: percentageActiveUsers,
      }
    })
  }, [formattedData, dates])

  return {
    loading,
    data: graph
  }
}

export default useGraph